/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Registry of full-screen, in-place OVERLAYS that must collapse on route change
 * and that the Back key closes before it navigates.
 *
 * Some overlays cover the whole viewport but are NOT routes (the mobile
 * FullPlayer, the chat pane when it's fullscreen on narrow screens), so a
 * navigation leaves them stranded over the new page. Each such overlay decides
 * for itself whether it's *currently* full-screen — one is detected via a mobile
 * flag, another via a viewport media query — but the close-on-navigate contract
 * is identical, so they share this one seam instead of each wiring its own
 * `onNavigate`.
 *
 * The seam is a neutral leaf (imports nothing app-specific): overlays register
 * from their own `onConnect`, and the router hook (AppView) calls
 * {@link closeFullScreenOverlaysForNavigation} on every completed nav. Knowledge
 * flows one way into this module — no overlay needs to know about the others,
 * and AppView needs to know about none of them.
 */

import type { Unsubscribe } from '@amazon/vinyl-util'

export interface FullScreenOverlay {
    /**
     * Is this overlay covering the whole viewport *right now*? Combines the
     * overlay's open state with its full-screen condition (mobile flag, media
     * query). A docked/rail/hidden overlay returns `false` so navigation leaves
     * it untouched.
     */
    isFullScreenNow(): boolean
    /** Collapse the overlay, revealing the page underneath (no route change). */
    close(): void
}

const overlays = new Set<FullScreenOverlay>()

/** Register an overlay; the returned {@link Unsubscribe} removes it. */
export function registerFullScreenOverlay(overlay: FullScreenOverlay): Unsubscribe {
    overlays.add(overlay)
    return () => {
        overlays.delete(overlay)
    }
}

/**
 * Close every registered overlay that is *currently* full-screen. Called from
 * the router's completed-navigation hook. Overlays that aren't full-screen (a
 * closed FullPlayer, a wide docked chat rail) are left as-is.
 */
export function closeFullScreenOverlaysForNavigation(): void {
    for (const overlay of overlays) {
        if (overlay.isFullScreenNow()) overlay.close()
    }
}

/** Close the most recently registered overlay that is full-screen now; false when none is. */
export function closeTopFullScreenOverlay(): boolean {
    const top = [...overlays].reverse().find((overlay) => overlay.isFullScreenNow())
    top?.close()
    return top != null
}
